import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { MobileNav } from './MobileNav';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';
import { CommandPalette } from '@/components/CommandPalette';

const COLLAPSE_STORAGE_KEY = 'tgc.sidebar.collapsed';

function readCollapsed(): boolean {
 if (typeof localStorage === 'undefined') return false;
 return localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1';
}

function writeCollapsed(value: boolean) {
 if (typeof localStorage === 'undefined') return;
 localStorage.setItem(COLLAPSE_STORAGE_KEY, value ? '1' : '0');
}

/**
 * Authenticated app chrome: persistent sidebar (lg+), mobile drawer, sticky
 * topbar, and a content area that renders nested routes via `<Outlet />`.
 *
 * The command palette wiring (Ctrl+K) is intentionally a stub for now and
 * will be completed by task 24.1.
 */
export function AppShell() {
 const { t } = useTranslation();
 const location = useLocation();
 const [mobileOpen, setMobileOpen] = useState(false);
 const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());
 const [paletteOpen, setPaletteOpen] = useState(false);
 const unreadCount = useUnreadNotifications();

 // Close the mobile drawer on every route change so the user lands on the
 // new page with a clean shell.
 useEffect(() => {
 setMobileOpen(false);
 }, [location.pathname]);

 // Persist the desktop collapsed state.
 useEffect(() => {
 writeCollapsed(collapsed);
 }, [collapsed]);

 // Reserve Ctrl+K / Cmd+K for the command palette.
 useEffect(() => {
 const onKey = (e: KeyboardEvent) => {
 if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
 e.preventDefault();
 setPaletteOpen((v) => !v);
 }
 };
 window.addEventListener('keydown', onKey);
 return () => window.removeEventListener('keydown', onKey);
 }, []);

 return (
 <TooltipProvider delayDuration={150}>
 <a
 href="#main-content"
 className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground"
 >
 {t('common.skip_to_content')}
 </a>

 <div className="flex min-h-screen w-full bg-background text-foreground">
 {/* Desktop sidebar */}
 <div className="hidden lg:block">
 <Sidebar
 collapsed={collapsed}
 onToggleCollapse={() => setCollapsed((v) => !v)}
 className="sticky top-0 h-screen"
 />
 </div>

 {/* Mobile drawer */}
 <MobileNav open={mobileOpen} onOpenChange={setMobileOpen} />

 <div className="flex min-w-0 flex-1 flex-col">
 <Topbar
 onOpenMobileNav={() => setMobileOpen(true)}
 notificationCount={unreadCount}
 onOpenCommandPalette={() => setPaletteOpen(true)}
 />
 <main
 id="main-content"
 tabIndex={-1}
 className="flex-1 focus:outline-none"
 >
 <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
 <Outlet />
 </div>
 </main>
 </div>
 </div>

 <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
 </TooltipProvider>
 );
}
